import { DESKTOP } from '@/src/constants/deviceTypes'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import { Airline } from '@/src/hooks/srp/airlineFilterManager'
import { DeviceType } from '@/src/types/deviceType'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Box, IconButton } from '@mui/material'
import { alpha } from '@mui/material/styles'
import React, { useEffect, useRef, useState } from 'react'
import AirlineCard from './airlineCard'

interface AirlineFilterProps {
    airlines?: Airline[]
    onSelect: (id: string) => void
    showArrows?: boolean // <-- add this prop
    deviceType: DeviceType
}

const CARD_WIDTH = 175
const CARD_MARGIN = 24 // px (1.5 * 16)
const VISIBLE_CARDS = 4.5
const SCROLL_AMOUNT = CARD_WIDTH * 2 + CARD_MARGIN

const AirlineFilter: React.FC<AirlineFilterProps> = ({
    airlines,
    onSelect,
    showArrows = true, // default to true for desktop
    deviceType = DESKTOP
}) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [isOverflowing, setIsOverflowing] = useState(false)

    const updateScrollButtons = () => {
        const el = scrollRef.current
        if (!el) return
        setCanScrollLeft(el.scrollLeft > 0)
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
        setIsOverflowing(el.scrollWidth > el.clientWidth + 1)
    }

    useEffect(() => {
        updateScrollButtons()
        const el = scrollRef.current
        if (!el) return
        el.addEventListener('scroll', updateScrollButtons)
        window.addEventListener('resize', updateScrollButtons)
        return () => {
            el.removeEventListener('scroll', updateScrollButtons)
            window.removeEventListener('resize', updateScrollButtons)
        }
    }, [])

    const handleScroll = (dir: 'left' | 'right') => {
        const el = scrollRef.current
        if (!el) return
        const amount = dir === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT
        el.scrollBy({ left: amount, behavior: 'smooth' })
    }

    // Calculate maxWidth for 4.5 cards
    const maxWidth = CARD_WIDTH * VISIBLE_CARDS + CARD_MARGIN * (VISIBLE_CARDS - 1)
    const isMobile = deviceType === 'MOBILE'
    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                minHeight: 60
            }}
        >
            {/* Left Arrow - now outside scrollable area */}
            {showArrows && isOverflowing && (
                <IconButton
                    onClick={() => handleScroll('left')}
                    disabled={!canScrollLeft}
                    sx={{
                        bgcolor: 'transparent',
                        width: 20,
                        height: 20,
                        p: 0,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'none',
                        border: 'none',
                        color: canScrollLeft ? 'grey.500' : 'grey.300',
                        cursor: canScrollLeft ? 'pointer' : 'default',
                        '&:hover': {
                            color: '#111',
                            bgcolor: 'transparent'
                        },
                        pointerEvents: canScrollLeft ? 'auto' : 'none',
                        mr: 1
                    }}
                >
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
            )}
            {/* Scrollable Cards */}
            <Box
                sx={{
                    position: 'relative',
                    maxWidth: { xs: '100%', md: `${maxWidth}px` },
                    width: '100%',
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 60
                }}
            >
                {/* Left Gradient Overlay (optional, subtle) */}
                {/* {isOverflowing && (
                    <Box
                        sx={theme => ({
                            position: 'absolute',
                            left: -2,
                            top: 0,
                            bottom: 0,
                            width: 20,
                            zIndex: 2,
                            pointerEvents: 'none',
                            background: `linear-gradient(to right, ${theme.palette.neutral[100]} 30%, ${alpha(theme.palette.neutral[100], 0.7)} 50%, ${alpha(theme.palette.neutral[100], 0)} 100%)`
                        })}
                    />
                )} */}
                {/* Right Gradient Overlay (optional, subtle) */}
                {/* {isOverflowing && (
                    <Box
                        sx={theme => ({
                            position: 'absolute',
                            right: -2,
                            top: 0,
                            bottom: 0,
                            width: 20,
                            zIndex: 2,
                            pointerEvents: 'none',
                            background: `linear-gradient(to left, ${theme.palette.neutral[100]} 30%, ${alpha(theme.palette.neutral[100], 0.7)} 50%, ${alpha(theme.palette.neutral[100], 0)} 100%)`
                        })}
                    />
                )} */}
                <Box
                    ref={scrollRef}
                    sx={{
                        maxWidth: { xs: '100%', md: `${maxWidth}px` },
                        width: '100%',
                        overflowX: 'auto',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        py: 1,
                        px: 1,
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                        boxSizing: 'border-box'
                    }}
                >
                    {airlines?.map(airline => (
                        <ErrorBoundaryWrapper
                            key={airline.id}
                            identifier={`airline-top-bar-filter-item-${airline.id}-${deviceType}`}
                            fallback={null}
                        >
                            <AirlineCard
                                {...airline}
                                onSelect={onSelect}
                                hoverBorderColor="grey.300"
                                isMobile={isMobile}
                            />
                        </ErrorBoundaryWrapper>
                    ))}
                </Box>
            </Box>
            {/* Right Arrow */}
            {showArrows && isOverflowing && (
                <IconButton
                    onClick={() => handleScroll('right')}
                    disabled={!canScrollRight}
                    sx={{
                        bgcolor: 'transparent',
                        width: 20,
                        height: 20,
                        p: 0,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'none',
                        border: 'none',
                        color: canScrollRight ? 'grey.500' : 'grey.300',
                        cursor: canScrollRight ? 'pointer' : 'default',
                        '&:hover': {
                            color: '#111',
                            bgcolor: 'transparent'
                        },
                        pointerEvents: canScrollRight ? 'auto' : 'none',
                        ml: 1
                    }}
                >
                    <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    )
}

export default AirlineFilter
